/**
 * Premium Light Theme — Surveyor's Club Mobile
 * 
 * Clean, high-contrast light palette optimized for:
 * - Readability in bright daylight conditions
 * - Modern, clean aesthetic (Fintech / Professional tier)
 */

export const light = {
  // ── Brand (Aligned with new colorful logo) ──────────────
  brandPrimary: '#0C5ADB',       // Royal Blue — primary actions, tab active, accents
  brandLight: '#DBEAFE',         // Soft blue — light badges, active indicators
  brandDark: '#0840A0',          // Deep blue — pressed states, gradients

  // ── Backgrounds ────────────────────────────────────────
  background: '#F8FAFC',         // Main screen background (premium Slate 50 light)
  surface: '#FFFFFF',            // Cards, list items, text fields, bottom sheets (pure white)
  surfaceElevated: '#FFFFFF',    // Elevated cards, modals, overlays
  surfaceHigh: '#F1F5F9',        // High-elevation surfaces, doc upload placeholders
  surfaceHover: '#F8FAFC',       // Hover/press states on surfaces

  // ── Borders ────────────────────────────────────────────
  border: '#E2E8F0',             // Dividers, boundaries, outline buttons
  borderLight: '#F1F5F9',        // Subtle borders for inputs
  borderFocus: '#0C5ADB',        // Focus ring for inputs

  // ── Text ───────────────────────────────────────────────
  textPrimary: '#0A1931',        // Headers, active status, primary inputs (Navy Blue)
  textSecondary: '#1C325B',      // Body text, secondary labels, placeholders (Lighter Navy)
  textTertiary: '#46638C',       // Disabled, hints, timestamps (Muted Navy)
  textOnBrand: '#FFFFFF',        // Text on brand-colored backgrounds
  textLink: '#0C5ADB',           // Interactive links

  // ── Semantic ───────────────────────────────────────────
  success: '#229A39',            // Completed states, active deals, positive 
  successLight: '#E6F4EA',       // Success background tint (light green)
  warning: '#F59E0B',            // Under review, counter-offers, negotiations
  warningLight: '#FEF3C7',       // Warning background tint (light amber)
  error: '#EF4444',              // Deleted actions, alerts, validation errors
  errorLight: '#FEE2E2',         // Error background tint (light red)
  info: '#3B82F6',               // Information badges, review states
  ui: '#3B82F6',                 // Default UI actions

  // ── State Colors ───────────────────────────────────────
  stateSubmitted: '#64748B',
  stateReview: '#3B82F6',
  stateApproved: '#229A39',
  stateAssigned: '#8B5CF6',
  stateNegotiating: '#F59E0B',
  stateConfirmed: '#229A39',
  stateClosed: '#64748B',
  stateDisputed: '#EF4444',

  // ── Chat ───────────────────────────────────────────────
  chatBubbleSelf: '#0C5ADB',     // Our messages — brand blue
  chatBubbleOther: '#F1F5F9',    // Their messages — light gray surface
  chatBubbleSelfText: '#FFFFFF',
  chatBubbleOtherText: '#0A1931',
  chatSystem: '#E2E8F0',
  chatBlocked: '#FEE2E2',

  // ── Interactive ────────────────────────────────────────
  overlay: 'rgba(15, 23, 42, 0.45)', // soft overlay
  skeleton: '#E2E8F0',
  shimmer: '#F1F5F9',

  // ── Tab Bar / Navigation ───────────────────────────────
  tabBar: '#FFFFFF',             // Bottom tab background
  tabBorder: '#E2E8F0',          // Top border of tab bar
  tabActive: '#0C5ADB',          // Active tab icon/label
  tabInactive: '#94A3B8',        // Inactive tab icon/label

  // ── Header ─────────────────────────────────────────────
  header: '#FFFFFF',             // Top header background
  headerBorder: '#E2E8F0',       // Bottom border of header

  // ── Card Accent Colors ─────────────────────────────────
  accentOrange: '#FF6B35',
  accentPurple: '#8B5CF6',
  accentTeal: '#24B5A8',
  accentPink: '#EC4899',
  accentGreen: '#229A39',
  accentBlue: '#3B82F6',

  // ── Gradients (start, end) ─────────────────────────────
  gradientBrand: ['#0C5ADB', '#0840A0'] as const,
  gradientHero: ['#FFFFFF', '#F8FAFC'] as const,
  gradientCard: ['#FFFFFF', '#FFFFFF'] as const,
};

export const darkThemeColors = { ...light };

export const dark = { ...light };

export function updateMobileTheme(mode: 'light' | 'dark') {
  Object.assign(dark, light);
}

export type DarkColors = typeof dark;
